import React from 'react';

import type { V1Amount } from '@/api/clients/ohip';
import { Box, Flex } from '@/box';
import { CurrencyPreview, useCurrencyPreview } from '@/currency-preview';
import type { IFinancialAmountInstance } from '@/finance';
import type { HotelConfiguration } from '@/hotel';
import { useTranslation } from '@/i18n';
import { useSpaceMatchingKey, useTheme } from '@/ui/theme';
import { BodyCopy, BodyLarge, BodySmall, Caption } from '@/ui/typography';

interface RoomTotalProps {
  isTotalInclusiveOfAllTaxes: boolean;
  totalWithTaxes?: IFinancialAmountInstance;
  // The message to display for included taxes (eg. (Incl. VAT))
  includedTaxesMessageTranslationKey: HotelConfiguration['legacyTaxes']['includedTaxesMessageTranslationKey'];
  price: V1Amount;
  priceWithoutDiscount?: V1Amount;
  taxLabel?: string;
}

export const RoomTotal = (props: RoomTotalProps) => {
  const margin = useSpaceMatchingKey('xxs');
  const { t } = useTranslation(['bookingSummary', 'taxesShortDescription']);
  const {
    colors,
    componentProperties: { bedroomCard },
  } = useTheme();
  const pricePreview = useCurrencyPreview({ amount: props.price });
  const totalWithTaxesPreview = useCurrencyPreview({
    amount: props.totalWithTaxes,
  });
  const isSubtotal = props.includedTaxesMessageTranslationKey === 'none';

  const accessibleLabel = isSubtotal
    ? t('bookingSummary:labels.subtotal', {
        price: pricePreview,
      })
    : t('taxesShortDescription:accessibleRoomTotalExplainer', {
        price: pricePreview,
        totalWithTaxes: totalWithTaxesPreview,
        includedTaxesMessage: `$t(taxesShortDescription:${props.includedTaxesMessageTranslationKey}Accessible)`,
        context: props.isTotalInclusiveOfAllTaxes ? '' : 'withAdvancedTaxes',
      });

  return (
    <div>
      <Flex
        alignItems="baseline"
        flexWrap="wrap"
        maxWidth={380}
        aria-label={accessibleLabel}
      >
        {!isSubtotal && (
          <Box marginInlineEnd={margin}>
            <BodySmall color="secondary300" css={bedroomCard?.totalLabel}>
              {t('bookingSummary:labels.total')}
            </BodySmall>
          </Box>
        )}
        <Flex
          alignItems="baseline"
          flexDirection="row"
          flexWrap="wrap"
          gap="8px"
        >
          <Flex gap="8px" alignItems="baseline">
            {props.priceWithoutDiscount && (
              <BodyCopy
                css={{
                  color: `${colors.sale} !important`,
                  textDecoration: 'line-through',
                }}
              >
                <CurrencyPreview amount={props.priceWithoutDiscount} />
              </BodyCopy>
            )}
            <BodyLarge
              testId="room-total-price"
              css={{
                ...bedroomCard?.totalPrice,
                ['@media all and (max-width: 639px)']: {
                  ...bedroomCard?.totalPrice?.mobile,
                },
              }}
            >
              {pricePreview}
            </BodyLarge>
          </Flex>
          {props.isTotalInclusiveOfAllTaxes && !isSubtotal && (
            <Caption
              noWrap
              color="secondary300"
              testId="room-total-taxes"
              css={bedroomCard?.totalLabel}
            >
              {`(${
                props.includedTaxesMessageTranslationKey === 'FREETEXT'
                  ? props.taxLabel
                  : t(
                      `taxesShortDescription:${props.includedTaxesMessageTranslationKey}`
                    )
              })`}
            </Caption>
          )}
          {!props.isTotalInclusiveOfAllTaxes ? (
            <Caption noWrap color="secondary300" testId="room-total-taxes">
              {`(${
                props.includedTaxesMessageTranslationKey === 'FREETEXT'
                  ? props.taxLabel
                  : t(
                      `taxesShortDescription:${props.includedTaxesMessageTranslationKey}`
                    )
              }): `}
              {totalWithTaxesPreview}
            </Caption>
          ) : undefined}
        </Flex>
      </Flex>
    </div>
  );
};
